import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { usePrimaryComplaintStore, ComplaintHistoryStep } from "@/store/primaryComplaint";
import { complaintHistoryLabels as t } from "./complaintHistoryLabels";
import { Button } from "@/components/ui/Button";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";
import { ChoiceGroup } from "@/components/forms/ChoiceGroup";
import { ComplaintStepHeader, StepLoadError } from "./ComplaintStepHeader";
import { useComplaintValues } from "@/hooks/useComplaintValues";
import { missingRequired, savedValue } from "@/utils/complaintSteps";

const SYMPTOMS_FIELD = "Symptoms" as const;
const NO_RADIATION = "None";
const SIDE_OPTIONS = [
  { value: "left", label: t.left },
  { value: "right", label: t.right },
  { value: "both", label: t.both },
];

type SymptomsMap = Record<string, string>;
type Radiates = "yes" | "no" | null;

/**
 * Saved Symptoms → answer. "None" is an explicit No; a JSON object naming at least one location is
 * Yes. "{}" (Yes with no location, which the chart shows as no radiation) and nothing saved are
 * unanswered, matching how older rows were read.
 */
function parseSymptoms(raw: string | undefined): { radiates: Radiates; symptoms: SymptomsMap } {
  if (!raw) return { radiates: null, symptoms: {} };
  if (raw === NO_RADIATION) return { radiates: "no", symptoms: {} };
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object" && Object.keys(parsed).length > 0) {
      return { radiates: "yes", symptoms: parsed as SymptomsMap };
    }
  } catch {
    // fall through: unreadable value counts as unanswered
  }
  return { radiates: null, symptoms: {} };
}

export function ComplaintHistory3() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const complaintIdParam = searchParams.get("complaintId");

  // Unanswered until the patient picks Yes or No; never defaults to "No" (a clinical answer).
  const [radiates, setRadiates] = useState<Radiates>(null);
  const [symptoms, setSymptoms] = useState<SymptomsMap>({});
  const [radiationLocations, setRadiationLocations] = useState<string[]>([]);
  const [locationsStatus, setLocationsStatus] = useState<"loading" | "error" | "ready">("loading");
  const [locationsAttempt, setLocationsAttempt] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [missing, setMissing] = useState<"radiates" | "radiation-locations" | null>(null);

  // This complaint's own body area: null until its header has loaded (never another complaint's
  // area or a body-map tap), "" when it genuinely has none.
  const region = usePrimaryComplaintStore((s) => {
    const header = s.complaintHeaders?.find((h) => String(h.id) === complaintIdParam);
    return header ? header.region || "" : null;
  });
  const updateComplaintValue = usePrimaryComplaintStore((s) => s.updateComplaintValue);
  const setComplaintHistoryStep = usePrimaryComplaintStore((s) => s.setComplaintHistoryStep);
  const fetchRadiatingList = usePrimaryComplaintStore((s) => s.fetchRadiatingList);
  const { values, status, retry } = useComplaintValues(complaintIdParam);

  useEffect(() => {
    if (status !== "ready" || !values) return;
    const saved = parseSymptoms(values.find((v) => v.code === SYMPTOMS_FIELD)?.value);
    setRadiates(saved.radiates);
    setSymptoms(saved.symptoms);
  }, [status, values]);

  useEffect(() => {
    if (region == null) {
      setLocationsStatus("loading");
      return;
    }
    if (!region) {
      // No body area to look places up for: same as an area with none listed.
      setRadiationLocations([]);
      setLocationsStatus("ready");
      return;
    }
    let cancelled = false;
    setLocationsStatus("loading");
    fetchRadiatingList(region)
      .then((data) => {
        if (cancelled) return;
        setRadiationLocations(Array.isArray(data) ? (data as string[]) : []);
        setLocationsStatus("ready");
      })
      .catch(() => {
        // A Yes needs a location, so a failed list must be retryable, never an empty list.
        if (!cancelled) setLocationsStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [region, fetchRadiatingList, locationsAttempt]);

  const save = (value: string) => void updateComplaintValue(SYMPTOMS_FIELD, value).catch(() => {});

  const answerRadiates = (v: string | null) => {
    if (v == null) return;
    const next = v as Radiates;
    setRadiates(next);
    setMissing(null);
    if (next === "no") {
      setSymptoms({});
      save(NO_RADIATION);
    } else if (Object.keys(symptoms).length > 0) {
      save(JSON.stringify(symptoms));
    }
    // A bare Yes saves nothing: "{}" reads as unanswered, so it would replace an earlier No with no
    // answer at all if the patient left before picking a place. setSide saves once one is picked.
  };

  const setSide = (location: string, side: string | null) => {
    const next = { ...symptoms };
    if (side) next[location] = side;
    else delete next[location];
    setSymptoms(next);
    if (missing === "radiation-locations" && Object.keys(next).length > 0) setMissing(null);
    save(JSON.stringify(next));
  };

  // No places listed for this area: a Yes can't name one, so it continues without saving a
  // location (radiation stays unanswered rather than reading as No) and the patient is pointed at
  // the notes on the next step.
  const noPlaces = locationsStatus === "ready" && radiationLocations.length === 0;
  // Only places this area offers count: one kept from before the area was changed is hidden and
  // can't be removed, so it must not be saved behind the patient's back.
  const listedSymptoms =
    locationsStatus === "ready"
      ? Object.fromEntries(Object.entries(symptoms).filter(([place]) => radiationLocations.includes(place)))
      : symptoms;
  const hasPlace = Object.keys(listedSymptoms).length > 0;

  const goBack = () => setComplaintHistoryStep(ComplaintHistoryStep.ComplaintHistory2);

  const saveAndContinue = async () => {
    if (status !== "ready") return;
    const firstMissing = missingRequired([
      { id: "radiates", answered: radiates != null },
      // Yes with no location reaches the chart as no radiation, so it needs at least one.
      { id: "radiation-locations", answered: radiates !== "yes" || hasPlace || noPlaces },
    ]);
    if (firstMissing) {
      setMissing(firstMissing as "radiates" | "radiation-locations");
      // The places picker isn't rendered while its list loads; fall back to the message itself.
      const el = document.getElementById(firstMissing) ?? document.getElementById(`${firstMissing}-error`);
      el?.scrollIntoView({ block: "center", behavior: "smooth" });
      el?.focus({ preventScroll: true });
      return;
    }
    setIsSaving(true);
    try {
      if (radiates === "no") await updateComplaintValue(SYMPTOMS_FIELD, NO_RADIATION);
      else if (hasPlace) await updateComplaintValue(SYMPTOMS_FIELD, JSON.stringify(listedSymptoms));
      // Yes with no place to name: clear any earlier answer (e.g. a saved No) rather than leave it.
      else if (savedValue(values, SYMPTOMS_FIELD) !== "") await updateComplaintValue(SYMPTOMS_FIELD, "");
    } catch {
      // updateComplaintValue already shows a toast; stay so the patient can retry.
      setIsSaving(false);
      return;
    }
    setIsSaving(false);
    navigate({ pathname: "/complaint/complaint-factors", search: searchParams.toString() });
  };

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content p-4">
        <div className="complaint-history-card">
          <ComplaintStepHeader complaintId={complaintIdParam} step="history3" />

          {status === "loading" && (
            <div className="wizard-skeleton" role="status" aria-busy="true" aria-label="Loading your answers">
              <span />
              <span />
              <span />
            </div>
          )}

          {status === "error" && <StepLoadError onRetry={retry} />}

          {status === "ready" && (
            <>
              <ChoiceGroup
                id="radiates"
                required
                legend={t.radiationQuestion}
                options={[
                  { value: "yes", label: t.yes },
                  { value: "no", label: t.no },
                ]}
                value={radiates}
                onChange={answerRadiates}
                error={missing === "radiates" ? t.answerToContinue : null}
              />

              {radiates === "yes" && locationsStatus === "error" && (
                <StepLoadError
                  message="We couldn't load the places it can travel to."
                  onRetry={() => setLocationsAttempt((n) => n + 1)}
                />
              )}

              {radiates === "yes" && noPlaces && <p className="choice-group__hint">{t.radiationNoPlaces}</p>}

              {radiates === "yes" && locationsStatus === "ready" && radiationLocations.length > 0 && (
                <fieldset
                  id="radiation-locations"
                  tabIndex={-1}
                  aria-describedby={missing === "radiation-locations" ? "radiation-locations-error" : undefined}
                  className="choice-group"
                >
                  <legend className="choice-group__legend">
                    {t.radiationWhere} <span className="choice-group__hint">({t.selectAllApply})</span>
                  </legend>
                <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
                  {radiationLocations.map((location) => (
                    <ChoiceGroup
                      key={location}
                      id={`radiation-${location.replace(/\W+/g, "-").toLowerCase()}`}
                      layout="segmented"
                      clearable
                      legend={location}
                      options={SIDE_OPTIONS}
                      value={symptoms[location] ?? null}
                      onChange={(side) => setSide(location, side)}
                    />
                  ))}
                </div>
                </fieldset>
              )}

              {/* Outside the picker so it shows even while the places are still loading. */}
              {missing === "radiation-locations" && (
                <p id="radiation-locations-error" className="choice-group__error" role="alert" tabIndex={-1}>
                  {t.pickWhereItTravels}
                </p>
              )}
            </>
          )}
        </div>
      </div>
      <WizardActionsBar>
        <Button type="button" variant="outline" onClick={goBack} aria-label={t.backButton}>
          ← {t.backButton}
        </Button>
        <Button type="button" onClick={saveAndContinue} disabled={isSaving || status !== "ready"}>
          {isSaving ? "Saving…" : t.continueButton}
        </Button>
      </WizardActionsBar>
    </div>
  );
}
